import { RangeFilterValue } from '../Filter/RangeFilterValue';
import { Asset } from '../Model/Asset';
import { Attribute } from '../Model/Attribute';
import { ProductInterface } from '../Model/Product';

interface Dictionary<T> {
    [index: string]: T | undefined
}

type SubCollection<T> = Map<string, T> | Dictionary<T>;

interface ProductLike {
    attributes: SubCollection<Attribute>
    assets: SubCollection<Asset>
    children?: (ProductInterface | ProductLike)[]
}

export class AttributeUtility {
    /**
     * Sort the given set of Attributes by their numeric value
     *
     * @param {Attribute[]} attributes
     * @return {Attribute[]}
     */
    public static sortByNumericValue(attributes: Attribute[]): Attribute[] {
        return attributes.sort(
            (a, b) => {
                if (a.numericValue < b.numericValue) {
                    return -1;
                }
                if (a.numericValue > b.numericValue) {
                    return 1;
                }
                return 0;
            }
        );
    }

    /**
     * Search the Product for an Attribute with the given `key`
     *
     * @param {ProductInterface | ProductLike | undefined} product
     * @param {string} key
     * @return {Attribute | undefined}
     */
    public static attributeForKey(
        product: ProductInterface | ProductLike | undefined,
        key: string
    ): Attribute | undefined {
        if (!product) {
            return undefined;
        }
        const attributes = product.attributes;
        if (!attributes) {
            return undefined;
        }

        return AttributeUtility.getFromSet(attributes, key) || AttributeUtility.getFromSet(attributes, 'A_PIM_' + key);
    }

    /**
     * Search the Product for an Asset with the given `key` and the preferred `locale`
     *
     * @param {ProductInterface | ProductLike | undefined} product
     * @param {string} key
     * @param {string} locale Preferred locale
     * @return {Asset | undefined}
     */
    public static getProductAsset(
        product: ProductInterface | ProductLike | undefined,
        key: string,
        locale: string
    ): Asset | undefined {
        if (!product) {
            return undefined;
        }
        const assets = product.assets;
        if (!assets) {
            return undefined;
        }

        if (key.substr(0, 6) !== 'A_PIM_') {
            return AttributeUtility.getProductAsset(product, 'A_PIM_' + key, locale);
        }

        return AttributeUtility.getFromSet(assets, key + '-' + locale)
            || AttributeUtility.getFromSet(assets, key + '-undefined_locale')
            || AttributeUtility.getFromSet(assets, key + '-de');
    }

    /**
     * Collect the Attribute `key` from all the Products in the collection and determine the minimum and maximum options
     * of the `numericValue` property
     *
     * If the collected attributes are empty `undefined` will be returned
     *
     * @param {(ProductInterface | ProductLike)[]} collection
     * @param {string} key
     * @return {{min: number ; max: number} | undefined}
     */
    public static getBounds(
        collection: (ProductInterface | ProductLike)[],
        key: string
    ): RangeFilterValue | undefined {
        const attributes = AttributeUtility.attributesForKeyFiltered(collection, key);
        if (attributes.length === 0) {
            return undefined;
        }

        const numericValues = attributes.map(a => a.numericValue);

        return {
            min: Math.min(...numericValues),
            max: Math.max(...numericValues),
        };
    }

    /**
     * Collect the Attribute `key` from all the Products in the collection
     *
     * @param {(ProductInterface | ProductLike)[]} collection
     * @param {string} key
     * @return {(Attribute | undefined)[]}
     */
    public static attributesForKey(
        collection: (ProductInterface | ProductLike)[],
        key: string
    ): (Attribute | undefined)[] {
        return collection.map(p => AttributeUtility.attributeForKey(p, key));
    }

    /**
     * Collect the Attribute `key` from all the Products in the collection and filter out `undefined`
     *
     * @param {(ProductInterface | ProductLike)[]} collection
     * @param {string} key
     * @return {Attribute[]}
     */
    public static attributesForKeyFiltered(
        collection: (ProductInterface | ProductLike)[],
        key: string
    ): Attribute [] {
        return AttributeUtility.attributesForKey(collection, key).filter(a => !!a) as Attribute[];
    }

    /**
     * Collect the Attribute `key` from each child of each Product in the collection
     *
     * @param {(ProductInterface | ProductLike)[]} collection
     * @param {string} key
     * @return {(Attribute | undefined)[]}
     */
    public static childAttributesForKey(
        collection: (ProductInterface | ProductLike)[],
        key: string
    ): (Attribute | undefined)[] {
        return collection.flatMap(product => {
            if (!product || !product.children) {
                return [];
            } else {
                return this.attributesForKey(product.children, key);
            }
        });
    }

    /**
     * Collect the Attribute `key` from each child of each Product in the collection and filter out `undefined`
     *
     * @param {(ProductInterface | ProductLike)[]} collection
     * @param {string} key
     * @return {Attribute[]}
     */
    public static childAttributesForKeyFiltered(
        collection: (ProductInterface | ProductLike)[],
        key: string
    ): Attribute [] {
        return collection.flatMap(product => {
            if (!product || !product.children) {
                return [];
            } else {
                return this.attributesForKeyFiltered(product.children, key);
            }
        }).filter(a => !!a) as Attribute[];
    }

    private static getFromSet<T>(collection: Map<string, T> | Dictionary<T>, key: string): T | undefined {
        if (collection instanceof Map) {
            return collection.get(key);
        } else {
            return (collection as Dictionary<T>)[key];
        }
    }
}
